/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { useScrollCoordinates } from '../../../hooks/useScrollCoordinates';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { theme } from '../../../domain/theme';
import { getOverflowClipping } from '../../../domain/utils/domUtils';
import { Actionable } from '../atoms/Actionable';
import { IconChevronLeft } from '../../Icon/IconChevronLeft';
import { IconChevronRight } from '../../Icon/IconChevronRight';
import { Fade } from '../atoms/Fade';
import { getColorsRGB } from '../../../domain/utils/colorUtils';
import { t } from '../../../domain/services/configService';
import { facePaint } from '../../../domain/theme/utils/facePaint';
import {
  numberOrStringOrArrayPlusNull,
  stringOrArrayPlusNull
} from '../../propTypes';

export const HorizontalScrollContainer = React.forwardRef(
  (
    {
      children,
      backgroundColor,
      arrow,
      hideArrows,
      gradient,
      align,
      width,
      maxWidth,
      margin
    },
    forwardedRef
  ) => {
    const colors = getColorsRGB(backgroundColor);
    const styles = {
      container: css`
        position: relative;
        ${Array.isArray(width) ? facePaint({ width }) : `width: ${width}`};
        max-width: ${maxWidth};
        margin: ${margin};
      `,
      scrollContainer: css`
        overflow-x: scroll;
        scroll-behavior: smooth;
        position: relative;
        text-align: ${align};
        -ms-overflow-style: none; /* Internet Explorer 10+ */
        scrollbar-width: none; /* Firefox */
        &::-webkit-scrollbar {
          display: none; /* Safari and Chrome */
        }
      `,
      iconContainer: css`
        position: absolute;
        top: 0;
        bottom: 0;
        /* Make Fade component use all available vertical space */
        > div {
          height: 100%;
        }
      `,
      iconContainerPrev: css`
        ${
          Array.isArray(arrow?.prev?.container)
            ? facePaint({ left: arrow.prev.container })
            : `left: 0}`
        }; }
      `,
      iconContainerNext: css`
        ${
          Array.isArray(arrow?.next?.container)
            ? facePaint({ right: arrow.next.container })
            : `right: 0}`
        }; }
      `,
      icons: css`
        min-width: ${hideArrows ? 30 : 60}px;
        width: 100%;
        height: 100%;
        font-size: ${theme.font.size('xl2')};
        line-height: ${theme.line.height('base')};
        button {
          ${arrow.top
            ? `position: relative; top: ${arrow.top};`
            : 'height:100%'}
        }
      `,
      prev: css`
        background: linear-gradient(
          90deg,
          rgba(${colors.background}, 1) ${gradient.prev[0]},
          rgba(255, 255, 255, 0) ${gradient.prev[1]}
        );
        button > svg {
          ${Array.isArray(arrow?.prev?.float)
            ? facePaint({ float: arrow.prev.float })
            : `float: ${arrow?.prev?.float ?? 'left'}`};
          ${Array.isArray(arrow?.prev?.margin)
            ? facePaint({ margin: arrow.prev.margin })
            : `margin: ${arrow?.prev?.margin ?? '13px 0 13px 7px'}`};
        }
      `,
      next: css`
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) ${gradient.next[0]},
          rgba(${colors.background}, 1) ${gradient.next[1]}
        );
        button > svg {
          ${Array.isArray(arrow?.next?.float)
            ? facePaint({ float: arrow.next.float })
            : `float: ${arrow?.next?.float ?? 'right'}`};
          ${Array.isArray(arrow?.next?.margin)
            ? facePaint({ margin: arrow.next.margin })
            : `margin: ${arrow?.next?.margin ?? '13px 7px 13px 0'}`};
        }
      `
    };

    const scrollContainerRef = useRef(null);
    /*     if (forwardedRef) {
      // eslint-disable-next-line no-param-reassign
      forwardedRef.current = scrollContainerRef?.current;
    } */

    const [overflowState, setOverflowState] = useState({
      left: false,
      right: false
    });

    const updateOverflowState = () => {
      const overflowClipping = getOverflowClipping(scrollContainerRef.current);
      setOverflowState({
        left: overflowClipping.left,
        right: overflowClipping.right
      });
    };

    const { x: elementInnerScrollXPosition } = useScrollCoordinates({
      ref: scrollContainerRef
    });
    const { width: windowWidth } = useWindowSize({
      triggerOnMount: true
    });
    useEffect(() => {
      updateOverflowState();
    }, [elementInnerScrollXPosition, windowWidth, children]);

    const scrollToTheRight = () => {
      scrollContainerRef.current.scrollLeft +=
        scrollContainerRef.current.clientWidth;
    };
    const scrollToTheLeft = () => {
      scrollContainerRef.current.scrollLeft -=
        scrollContainerRef.current.clientWidth;
    };

    return (
      <div css={styles.container} ref={forwardedRef}>
        <div css={[styles.iconContainer, styles.iconContainerPrev]}>
          <Fade
            show={overflowState.left}
            position="relative"
            zIndex="3"
            duration={0.15}
          >
            <div css={[styles.icons, styles.prev]}>
              {!hideArrows && (
                <Actionable title={t('Left')} onClick={scrollToTheLeft}>
                  <IconChevronLeft
                    size={arrow.size}
                    display="block"
                    color={arrow.color}
                  />
                </Actionable>
              )}
            </div>
          </Fade>
        </div>
        <div css={[styles.iconContainer, styles.iconContainerNext]}>
          <Fade
            show={overflowState.right}
            position="relative"
            zIndex="3"
            duration={0.15}
          >
            <div css={[styles.icons, styles.next]}>
              {!hideArrows && (
                <Actionable title={t('Right')} onClick={scrollToTheRight}>
                  <IconChevronRight
                    size={arrow.size}
                    display="block"
                    color={arrow.color}
                  />
                </Actionable>
              )}
            </div>
          </Fade>
        </div>
        {/* scroll-container class is used by external forwardedRef */}
        <div
          ref={scrollContainerRef}
          css={styles.scrollContainer}
          className="scroll-container"
        >
          {children}
        </div>
      </div>
    );
  }
);

HorizontalScrollContainer.defaultProps = {
  backgroundColor: 'white',
  arrow: {
    color: 'black',
    size: 18
  },
  hideArrows: false,
  gradient: { prev: ['40%', '100%'], next: ['0%', '60%'] },
  align: 'left',
  width: null,
  maxWidth: null,
  margin: null
};

HorizontalScrollContainer.propTypes = {
  children: PropTypes.node.isRequired,
  backgroundColor: PropTypes.string,
  arrow: PropTypes.shape({
    color: PropTypes.string,
    top: PropTypes.string,
    size: PropTypes.number,
    prev: PropTypes.shape({
      container: numberOrStringOrArrayPlusNull,
      margin: numberOrStringOrArrayPlusNull,
      float: numberOrStringOrArrayPlusNull
    }),
    next: PropTypes.shape({
      container: numberOrStringOrArrayPlusNull,
      margin: numberOrStringOrArrayPlusNull,
      float: numberOrStringOrArrayPlusNull
    })
  }),
  hideArrows: PropTypes.bool,
  gradient: PropTypes.shape({
    prev: PropTypes.arrayOf(PropTypes.string),
    next: PropTypes.arrayOf(PropTypes.string)
  }),
  align: PropTypes.oneOf(['left', 'center', 'right']),
  width: stringOrArrayPlusNull,
  maxWidth: PropTypes.string,
  margin: PropTypes.string
};
